
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DeviceService } from '@devices';
import { LocationOptionsComponent } from '@locations';

import { ConnectedDevicesOptionsComponent, DataPointOptionsComponent, DeviceStatsOptionsComponent, GaugeOptionsComponent, RoomStateOptionsComponent } from '../../components';
import { WidgetConfig, WidgetDataPoint } from '../../models';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ConnectedDevicesOptionsComponent,
    DataPointOptionsComponent,
    DeviceStatsOptionsComponent,
    GaugeOptionsComponent,
    LocationOptionsComponent,
    MatDialogModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    RoomStateOptionsComponent,
  ],
  selector: 'avs-li-widget-dialog',
  templateUrl: './widget-dialog.component.html',
})
export class WidgetDialogComponent {
  protected data = inject<WidgetConfig>(MAT_DIALOG_DATA);
  protected form: FormGroup;

  private deviceService = inject(DeviceService);
  private dialogRef = inject(MatDialogRef);
  private fb = inject(FormBuilder);

  constructor() {
    this.form = this.fb.group({
      name: [this.data?.name, Validators.required],
      type: [this.data?.type, Validators.required],
      widgetConfig: [this.data, Validators.required],
    });

    this.form.get('type')!
      .valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.form.patchValue({ widgetConfig: null }));
  }

  protected onSubmit() {
    const { name, type, widgetConfig } = this.form.value;

    this.dialogRef.close({ ...widgetConfig, name, type });
  }

  protected updateForm(widgetConfig: Partial<WidgetConfig> | null) {
    const locationId = (widgetConfig as WidgetDataPoint)?.locationId;
    const deviceId = (widgetConfig as WidgetDataPoint)?.deviceId;

    this.form.patchValue({ widgetConfig });

    if (locationId && deviceId) {
      this.deviceService
        .getDeviceDetailsByDeviceId(deviceId)
        .subscribe(device => this.form.patchValue({ name: device.friendlyName }));
    }
  }
}
